import React from 'react';
import { formatDate } from '../analyticsUtils';
import {
  Badge,
  Btn,
  Card,
  CardHead,
  GhostBtn,
  Hint,
  Input,
  Mono,
  Row,
  Select,
  Skeleton,
  Stack,
  Table,
  TableWrap,
} from '../AnalyticsAdmin.styles';
import RowMenu from './RowMenu';

const PlatformView = ({
  workspaces,
  plans,
  onUpdatePlan,
  onUpdateStatus,
  onRenameWorkspace,
  onDeleteWorkspace,
  onOpenWorkspace,
  newWorkspaceName,
  setNewWorkspaceName,
  newWorkspaceOwnerEmail,
  setNewWorkspaceOwnerEmail,
  newWorkspacePlanId,
  setNewWorkspacePlanId,
  workspaceSaving,
  onCreateWorkspace,
  admins,
  adminsLoading,
  newAdminEmail,
  setNewAdminEmail,
  adminSaving,
  onAddAdmin,
  onRemoveAdmin,
}) => (
  <Stack $gap="1.25rem">
    <Card $flush>
      <CardHead>
        <h2>Customer workspaces</h2>
        <Mono>{workspaces.length} workspaces</Mono>
      </CardHead>
      <TableWrap>
        <Table $min="44rem">
          <thead>
            <tr>
              <th>Workspace</th>
              <th>Owner</th>
              <th>Usage</th>
              <th>Plan</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {workspaces.map((w) => (
              <tr key={w.id}>
                <td>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{w.name}</div>
                  <Mono style={{ fontSize: '0.6875rem' }}>{w.id}</Mono>
                </td>
                <td>{w.owner_email || '-'}</td>
                <td style={{ fontSize: '0.8125rem' }}>
                  {w.project_count}/{w.limits?.maxProjects ?? 1} projects · {w.member_count}/{w.limits?.maxMembers ?? 3} members
                </td>
                <td>
                  <Select aria-label={`Plan for ${w.name}`} value={w.plan_id || 'starter'} onChange={(e) => onUpdatePlan(w.id, e.target.value)}>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>{plan.name}</option>
                    ))}
                  </Select>
                </td>
                <td>
                  <Row style={{ flexWrap: 'nowrap' }}>
                    <Badge $danger={w.status && w.status !== 'active'}>{w.status || 'active'}</Badge>
                  </Row>
                </td>
                <td className="actions">
                  <RowMenu
                    label={`More actions for ${w.name}`}
                    items={[
                      { label: 'Open workspace', onClick: () => onOpenWorkspace(w.id) },
                      { label: 'Rename', onClick: () => onRenameWorkspace(w) },
                      w.status === 'suspended'
                        ? { label: 'Reactivate', onClick: () => onUpdateStatus(w.id, 'active') }
                        : { label: 'Suspend', onClick: () => onUpdateStatus(w.id, 'suspended') },
                      { label: 'Delete workspace…', danger: true, onClick: () => onDeleteWorkspace(w) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
      <form onSubmit={onCreateWorkspace} style={{ padding: '0.875rem 1.125rem', borderTop: '1px solid var(--border)' }}>
        <Row>
          <Input aria-label="Workspace name" placeholder="Workspace name" value={newWorkspaceName} onChange={(e) => setNewWorkspaceName(e.target.value)} $grow />
          <Input
            aria-label="Owner email"
            type="email"
            placeholder="Owner email"
            value={newWorkspaceOwnerEmail}
            onChange={(e) => setNewWorkspaceOwnerEmail(e.target.value)}
            $grow
          />
          <Select aria-label="Plan" value={newWorkspacePlanId} onChange={(e) => setNewWorkspacePlanId(e.target.value)}>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>{plan.name}</option>
            ))}
          </Select>
          <Btn type="submit" disabled={workspaceSaving || !newWorkspaceName.trim() || !newWorkspaceOwnerEmail.trim()}>
            {workspaceSaving ? 'Creating…' : 'Create workspace'}
          </Btn>
        </Row>
      </form>
    </Card>

    <Card $flush>
      <CardHead>
        <h2>Platform admins</h2>
        <Mono>Can create and control every workspace</Mono>
      </CardHead>
      {adminsLoading ? (
        <div style={{ padding: '1rem 1.125rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Skeleton $w="40%" />
          <Skeleton $w="30%" />
        </div>
      ) : admins.length === 0 ? (
        <Hint style={{ padding: '1rem 1.125rem', margin: 0 }}>No extra admins. Access comes from the server allowlist.</Hint>
      ) : (
        <TableWrap>
          <Table $min="28rem">
            <tbody>
              {admins.map((row) => (
                <tr key={row.email}>
                  <td className="strong">{row.email}</td>
                  <td>Added {formatDate(row.created_at)}</td>
                  <td className="actions">
                    <Row $justify="flex-end" style={{ flexWrap: 'nowrap' }}>
                      <GhostBtn type="button" $small onClick={() => onRemoveAdmin(row.email)}>
                        Remove
                      </GhostBtn>
                      <RowMenu label={`More actions for ${row.email}`} items={[{ label: 'Force reset access', danger: true, onClick: () => onRemoveAdmin(row.email, true) }]} />
                    </Row>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
      <form onSubmit={onAddAdmin} style={{ padding: '0.875rem 1.125rem', borderTop: '1px solid var(--border)' }}>
        <Row>
          <Input aria-label="New admin email" type="email" placeholder="new-admin@example.com" value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} $grow />
          <GhostBtn type="submit" disabled={adminSaving || !newAdminEmail.trim()}>
            {adminSaving ? 'Adding…' : 'Add admin'}
          </GhostBtn>
        </Row>
      </form>
    </Card>
  </Stack>
);

export default PlatformView;
